import React, { useState, useEffect, useRef } from 'react';
import { setSeo } from '../utils/seo.js';
import PageHero3D from '../components/PageHero3D.jsx';
import ServiceShowcase from '../components/ServiceShowcase.jsx';
import AIWorkflowShowcase from '../components/AIWorkflowShowcase.jsx';

/**
 * Accordion component for Software Development section
 */
const SoftwareDevAccordion = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const accordionItems = [
    {
      title: "Frontend Development",
      description: "Modern web applications built with cutting-edge frameworks. We create responsive, interactive interfaces with exceptional user experience and optimal performance across all devices and platforms."
    },
    {
      title: "Backend Development",
      description: "Robust server architecture with secure APIs, efficient databases, and cloud-native deployment. We build scalable backend systems that handle your business logic and data with enterprise-grade security."
    },
    {
      title: "Mobile Applications",
      description: "Cross-platform mobile apps with native performance, offline capabilities, and seamless user experiences. We deliver apps that work flawlessly on both iOS and Android platforms."
    },
    {
      title: "System Integration",
      description: "Seamless connections between your software, CRMs, AI systems, and third-party services. We create unified workflows that streamline your business operations and eliminate data silos."
    }
  ];

  return (
    <div className="software-dev-accordion">
      {accordionItems.map((item, index) => (
        <div key={index} className="accordion-item">
          <div
            className={`accordion-header ${activeIndex === index ? 'active' : ''}`}
            onClick={() => setActiveIndex(activeIndex === index ? -1 : index)}
          >
            <span className="accordion-number">{String(index + 1).padStart(2, '0')}</span>
            <h4>{item.title}</h4>
            <span className="accordion-arrow">{activeIndex === index ? '−' : '+'}</span>
          </div>
          <div className={`accordion-content ${activeIndex === index ? 'active' : ''}`}>
            <p>{item.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Typewriter effect component
 */
const TypewriterText = ({ text, speed = 50 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            setIsTyping(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [hasAnimated]);

  useEffect(() => {
    if (isTyping && displayedText.length < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, displayedText.length + 1));
      }, speed);
      return () => clearTimeout(timeout);
    } else if (displayedText.length === text.length) {
      setIsTyping(false);
    }
  }, [displayedText, isTyping, text, speed]);

  return (
    <span ref={elementRef} className="typewriter-text">
      {displayedText}
      {isTyping && <span className="typewriter-cursor">|</span>}
    </span>
  );
};

/**
 * Services page with three main service categories based on original content.
 * Restructured with proper sections, cards for benefits/services, and highlighted MCP integration.
 */
const Solutions = () => {
  useEffect(() => {
    setSeo({
      title: 'Services | Aakeeo Technologies - CRM, AI & Software Solutions',
      description: 'Expert services in CRM consultations, AI workflow automation, and custom software development. Transform your business with SuiteCRM implementation, MCP integration, and cutting-edge AI solutions.',
      keywords: ['CRM consulting', 'SuiteCRM services', 'AI workflow automation', 'custom software development', 'MCP integration', 'business automation', 'CRM implementation'],
      canonical: window.location.href,
      og: {
        title: 'CRM, AI & Software Development Services | Aakeeo Technologies',
        description: 'Transform your business with our expert CRM consulting, AI automation, and custom software development services. SuiteCRM specialists with MCP integration expertise.',
        type: 'website'
      },
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Service',
        'serviceType': 'CRM Consulting and AI Automation',
        'provider': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies',
          'url': 'https://aakeeo.com'
        },
        'areaServed': 'Worldwide',
        'hasOfferCatalog': {
          '@type': 'OfferCatalog',
          'name': 'Technology Services',
          'itemListElement': [
            {
              '@type': 'Offer',
              'itemOffered': {
                '@type': 'Service',
                'name': 'CRM Consultations',
                'description': 'SuiteCRM implementation and customization services'
              }
            },
            {
              '@type': 'Offer',
              'itemOffered': {
                '@type': 'Service',
                'name': 'AI Workflow Automation',
                'description': 'AI-powered workflow automation and integration'
              }
            },
            {
              '@type': 'Offer',
              'itemOffered': {
                '@type': 'Service',
                'name': 'Custom Software Development',
                'description': 'Full-stack software development and system integration'
              }
            }
          ]
        }
      }
    });
  }, []);


  return (
    <div className="solutions-page">
      <PageHero3D
        title="Comprehensive Services to Transform Your Business"
        subtitle="CRM consultations, AI workflow automation, and custom software development."
        accent="#48cae4"
      />

      {/* 1. CRM Consultations Section - Dark Theme */}
      <section className="crm-section-dark">
        <div className="crm-background-pattern"></div>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
            <h2 className="crm-section-title">
              CRM Consultations
            </h2>
            <p className="crm-section-subtitle">
              Our SuiteCRM Consultation service is tailored to help businesses unlock the full potential of open-source technology. We streamline operations, enhance customer engagement, and deliver measurable results.
            </p>
          </div>

          {/* Why SuiteCRM? */}
          <div style={{ marginBottom: '5rem' }}>
            <h3 className="crm-subsection-title">
              Why SuiteCRM?
            </h3>
            <div className="crm-benefits-grid">

              <div className="crm-benefit-card">
                <div className="crm-card-glow"></div>
                <div className="crm-icon-container">
                  <i className="fa-solid fa-dollar-sign"></i>
                </div>
                <h4>No Per-User, Per-Month Fees</h4>
                <p>
                  Avoid the restrictive costs associated with traditional CRMs. SuiteCRM eliminates per-user licensing, saving you substantial expenses as your team grows.
                </p>
              </div>

              <div className="crm-benefit-card">
                <div className="crm-card-glow"></div>
                <div className="crm-icon-container">
                  <i className="fa-solid fa-cogs"></i>
                </div>
                <h4>Customizable to Your Processes</h4>
                <p>
                  Tailor every aspect of your CRM to your unique business processes, ensuring it aligns perfectly with your operations.
                </p>
              </div>

              <div className="crm-benefit-card">
                <div className="crm-card-glow"></div>
                <div className="crm-icon-container">
                  <i className="fa-solid fa-shield-alt"></i>
                </div>
                <h4>Full Data Ownership</h4>
                <p>
                  Retain complete control over your data with a self-hosted solution. Say goodbye to vendor lock-in and ensure compliance with your privacy policies.
                </p>
              </div>

              <div className="crm-benefit-card">
                <div className="crm-card-glow"></div>
                <div className="crm-icon-container">
                  <i className="fa-solid fa-piggy-bank"></i>
                </div>
                <h4>Cost-Effective</h4>
                <p>
                  No hefty licensing fees - redirect your budget towards innovation and growth instead of paying for proprietary software.
                </p>
              </div>

              <div className="crm-benefit-card">
                <div className="crm-card-glow"></div>
                <div className="crm-icon-container">
                  <i className="fa-solid fa-users"></i>
                </div>
                <h4>Community-Driven Innovation</h4>
                <p>
                  Join a thriving global community that continuously improves and enhances SuiteCRM. Benefit from rapid updates and a pool of shared expertise.
                </p>
              </div>

              <div className="crm-benefit-card">
                <div className="crm-card-glow"></div>
                <div className="crm-icon-container">
                  <i className="fa-solid fa-expand-arrows-alt"></i>
                </div>
                <h4>Scalable and Flexible</h4>
                <p>
                  Adaptable to businesses of any size, SuiteCRM grows with you, from startup to enterprise.
                </p>
              </div>

            </div>
          </div>

          {/* Our Services - New Design */}
          <ServiceShowcase />

          {/* MCP-Enabled CRM Enhancements - Highlighted Section */}
          <div className="crm-mcp-section">
            <div className="crm-mcp-background-pattern"></div>
            <div className="crm-mcp-content">
              <div className="crm-mcp-header">
                <div className="crm-mcp-icon">
                  <i className="fa-solid fa-brain"></i>
                </div>
                <h3 className="crm-mcp-title">
                  MCP-Enabled CRM Enhancements
                </h3>
                <div className="crm-mcp-underline"></div>
              </div>

              <p className="crm-mcp-description">
                We go beyond traditional CRM services with our <strong className="mcp-highlight">MCP (Model Context Protocol)</strong> server integration.
                By connecting SuiteCRM with Large Language Model (LLM) APIs, your CRM becomes an <strong className="ai-highlight">AI-powered business assistant</strong> capable of:
              </p>

              <div className="crm-mcp-grid">
                <div className="crm-mcp-card">
                  <div className="crm-mcp-card-icon">
                    <i className="fa-solid fa-comments"></i>
                  </div>
                  <h4>Natural Language CRM Actions</h4>
                  <p>
                    Creating and updating leads, contacts, and opportunities through natural language commands.
                  </p>
                </div>

                <div className="crm-mcp-card">
                  <div className="crm-mcp-card-icon">
                    <i className="fa-solid fa-envelope"></i>
                  </div>
                  <h4>Intelligent Communication</h4>
                  <p>
                    Sending intelligent messages and emails directly from SuiteCRM with AI-generated content.
                  </p>
                </div>

                <div className="crm-mcp-card">
                  <div className="crm-mcp-card-icon">
                    <i className="fa-solid fa-chart-line"></i>
                  </div>
                  <h4>Real-Time Analytics</h4>
                  <p>
                    Delivering real-time analytics in the form of interactive graphs and charts.
                  </p>
                </div>
              </div>

              <div className="crm-mcp-cta">
                <p>
                  Transform your CRM from a data system into a decision-making hub that adapts to your business in real time.
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* Floating Elements */}
        <div className="crm-floating-element crm-float-1"></div>
        <div className="crm-floating-element crm-float-2"></div>
        <div className="crm-floating-element crm-float-3"></div>
      </section>

      {/* 2. AI Workflow Consultations Section */}
      <section className="section reveal" style={{ padding: '5rem 2rem', background: 'rgba(230, 57, 70, 0.05)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{
              fontSize: '2.5rem',
              marginBottom: '1rem',
              background: 'linear-gradient(135deg, rgba(2, 62, 138, 0.9), rgba(230, 57, 70, 0.9))',
              color: '#ffffff',
              fontWeight: '700',
              padding: '1rem 2rem',
              borderRadius: '15px',
              display: 'inline-block',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              AI Workflow Consultations
            </h2>
            <p style={{ color: 'var(--text-light)', fontSize: '1.2rem', maxWidth: '800px', margin: '0 auto' }}>
              Unlock the power of AI with our comprehensive consultation services. From integrating AI Agents to optimizing workflows, we deliver solutions that promise measurable impact.
            </p>
          </div>

          <div style={{ marginBottom: '3rem' }}>

            <h3 style={{ color: 'var(--secondary-color)', fontSize: '2rem', textAlign: 'center', marginBottom: '1rem' }}>

              Why Choose AI Workflow Consultation?

            </h3>

            <div className="crm-mcp-underline" style={{ margin: "0 auto" }}></div>
          </div>



          <AIWorkflowShowcase />

        </div>
      </section>

      {/* 3. Software Development Section - Professional Theme */}
      <section className="software-dev-section-dark">
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
          <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
            <h2 className="software-dev-section-title">
              Software Development
            </h2>
            <p className="software-dev-subtitle">
              We craft cutting-edge software solutions with precision and innovation. From concept to deployment,
              our development process transforms your business requirements into powerful, scalable applications.
            </p>
          </div>

          <SoftwareDevAccordion />

        </div>
      </section>

      {/* CTA Section */}
      <section className="section reveal" style={{ background: 'rgba(230, 57, 70, 0.1)', padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 className="cta-heading">
            <TypewriterText text="Ready to Transform Your Business?" speed={80} />
          </h2>
          <p style={{ color: 'var(--text-light)', marginBottom: '2rem', fontSize: '1.1rem' }}>
            Let's discuss your specific needs and create a custom solution that fits your workflow perfectly.
          </p>
          <button
            className="btn-primary"
            style={{ fontSize: '1.1rem', padding: '1rem 2.5rem' }}
            onClick={() => window.location.href = '/contact'}
          >
            Get Started Today
          </button>
        </div>
      </section>

    </div>
  );
};

export default Solutions;

